/**
 * Eigenbau: Rezeptpflege in den Einstellungen.
 *
 * Ein Rezept ist die Vorschrift, nach der eine Durchsicht prüft: ein Prompt
 * und die Lesewerkzeuge, die dabei erlaubt sind. Bis hierher ließ es sich nur
 * über MCP oder einen direkten tRPC-Aufruf anlegen — die Oberfläche konnte es
 * ausschließlich **auswählen**. In der Datenbank standen deshalb acht Rezepte,
 * alle mit Namen „Standard" und je einer Fassung.
 *
 * **Fassungen sind unveränderlich.** `durchsicht_laeufe.rezeptFassungId` zeigt
 * auf genau eine; änderte sie sich unter einem Lauf, bekäme ein Befund von
 * gestern rückwirkend eine andere Begründung. Bearbeiten heißt hier deshalb
 * „nächste Fassung anlegen", und der Verlauf bleibt lesbar.
 *
 * Der Name ist Beiwerk und darf sich ändern — er steht an der Sache, nicht in
 * ihr.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Leerzustand } from "@/components/Leerzustand";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { api } from "@/lib/api";
import { fehlerMeldung } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import { cx, focusRing } from "@/lib/utils";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  LadenZeile,
} from "./gemeinsam";

function zeitpunkt(wert: string | Date | null | undefined): string {
  if (!wert) return "—";
  return new Date(wert).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function EinstellungenRezepte() {
  const client = useQueryClient();
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [offenesRezept, setzeOffenesRezept] = useState<string | null>(null);
  const [mitArchiv, setzeMitArchiv] = useState(false);

  /* Entwurf der nächsten Fassung — je Rezept, damit nichts überschrieben wird. */
  const [entwurf, setzeEntwurf] = useState<{
    prompt: string;
    werkzeuge: string[];
    notiz: string;
  } | null>(null);
  const [neuerName, setzeNeuerName] = useState("");

  const rezepte = useQuery({
    queryKey: ["cubic", "durchsicht", "rezepte", mitArchiv],
    queryFn: () => api.durchsicht.rezepte.query({ mitArchiv }),
  });
  const werkzeuge = useQuery({
    queryKey: ["cubic", "durchsicht", "lesewerkzeuge"],
    queryFn: () => api.durchsicht.lesewerkzeuge.query(),
  });

  const neuLaden = async () => {
    await client.invalidateQueries({
      queryKey: ["cubic", "durchsicht", "rezepte"],
    });
  };

  const anlegen = useMutation({
    mutationFn: (name: string) =>
      api.durchsicht.rezeptAnlegen.mutate({
        name,
        // Ein leeres Rezept wäre keins; die erste Fassung trägt einen Platzhalter,
        // den die Maske sofort zum Überschreiben anbietet.
        prompt: "Prüfe die Änderung und melde, was auffällt.",
      }),
    onSuccess: async (ergebnis) => {
      setzeMeldung(null);
      setzeNeuerName("");
      await neuLaden();
      setzeOffenesRezept(ergebnis.rezeptId);
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const fassungAnlegen = useMutation({
    mutationFn: (eingabe: {
      rezeptId: string;
      prompt: string;
      werkzeuge: string[];
      notiz?: string;
    }) => api.durchsicht.rezeptFassungAnlegen.mutate(eingabe),
    onSuccess: async (_ergebnis, eingabe) => {
      setzeMeldung(null);
      /*
       * Der Entwurf wird auf das Gespeicherte gesetzt, nicht geleert.
       *
       * `null` wäre naheliegend — die Fassung ist ja fort. Nur zeigt die Maske
       * dann ein leeres Prompt-Feld und keine Werkzeuge, und das liest sich wie
       * „deine Eingabe ist verloren". Stehen bleibt, was gerade gespeichert
       * wurde; nur die Notiz gehört zur einzelnen Fassung und wird geleert.
       */
      setzeEntwurf({
        prompt: eingabe.prompt,
        werkzeuge: eingabe.werkzeuge,
        notiz: "",
      });
      await neuLaden();
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const umbenennen = useMutation({
    mutationFn: (eingabe: { rezeptId: string; name: string }) =>
      api.durchsicht.rezeptUmbenennen.mutate(eingabe),
    onSuccess: async () => {
      setzeMeldung(null);
      await neuLaden();
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const archiv = useMutation({
    mutationFn: (eingabe: { rezeptId: string; archiviert: boolean }) =>
      api.durchsicht.rezeptArchiv.mutate(eingabe),
    onSuccess: async () => {
      setzeMeldung(null);
      await neuLaden();
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const liste = rezepte.data ?? [];

  return (
    <EinstellungenHuelle
      aktiv="rezepte"
      beschreibung="Prüfvorschriften für Durchsicht und Auto Q&A."
    >
      <EinstellungsAbschnitt
        titel="Rezepte"
        beschreibung="Ein Rezept sagt, wonach eine Durchsicht sucht und welche Lesewerkzeuge sie dabei benutzen darf. Fassungen sind unveränderlich — wer etwas ändert, legt die nächste an, damit ein Lauf von gestern seine Begründung behält."
      >
        {meldung ? <Fehlerzeile meldung={meldung} /> : null}

        {rezepte.isLoading ? (
          <LadenZeile text="Lade Rezepte …" />
        ) : liste.length === 0 ? (
          <Leerzustand
            titel="Noch kein Rezept."
            hinweis="Ohne Rezept prüft eine Durchsicht nach der Standardvorschrift. Ein eigenes Rezept lohnt sich, sobald du wiederkehrend nach etwas Bestimmtem suchst."
          />
        ) : (
          <ul role="list" className="space-y-px">
            {liste.map((rezept) => {
              const offen = offenesRezept === rezept.id;
              const neueste = rezept.neuesteFassung;
              return (
                <li key={rezept.id} className="border-b border-linie">
                  <button
                    type="button"
                    aria-expanded={offen}
                    onClick={() => {
                      const naechstes = offen ? null : rezept.id;
                      setzeOffenesRezept(naechstes);
                      setzeEntwurf(
                        naechstes && neueste
                          ? {
                              prompt: neueste.prompt,
                              werkzeuge: [...neueste.werkzeuge],
                              notiz: "",
                            }
                          : null,
                      );
                    }}
                    className={cx(
                      "flex w-full flex-wrap items-baseline gap-x-4 gap-y-1 py-3.5 text-left",
                      focusRing,
                    )}
                  >
                    <span className="text-[14px] text-tinte">{rezept.name}</span>
                    {rezept.archiviertAm ? (
                      <Mikrolabel grad={11}>ausgemustert</Mikrolabel>
                    ) : null}
                    <span className="ml-auto flex flex-wrap items-baseline gap-x-4">
                      <Mikrolabel grad={11} zahl>
                        Fassung {neueste?.version ?? 0}
                      </Mikrolabel>
                      <Mikrolabel grad={11} zahl>
                        {neueste?.werkzeuge.length ?? 0} Werkzeuge
                      </Mikrolabel>
                      <Mikrolabel grad={11} zahl>
                        {rezept.verwendungen}× benutzt
                      </Mikrolabel>
                    </span>
                  </button>

                  {offen ? (
                    <div className="space-y-5 pb-6">
                      {/* Name — Beiwerk, deshalb sofort änderbar. */}
                      <div className="flex flex-wrap items-end gap-2.5">
                        <div className="min-w-[260px] flex-1">
                          <Label htmlFor={`name-${rezept.id}`}>Name</Label>
                          <Input
                            id={`name-${rezept.id}`}
                            className="mt-1.5"
                            defaultValue={rezept.name}
                            onBlur={(ereignis) => {
                              const wert = ereignis.target.value.trim();
                              if (wert && wert !== rezept.name) {
                                umbenennen.mutate({
                                  rezeptId: rezept.id,
                                  name: wert,
                                });
                              }
                            }}
                          />
                        </div>
                        <Button
                          variant="secondary"
                          onClick={() =>
                            archiv.mutate({
                              rezeptId: rezept.id,
                              archiviert: rezept.archiviertAm === null,
                            })
                          }
                          disabled={archiv.isPending}
                        >
                          {rezept.archiviertAm
                            ? "Zurückholen"
                            : "Ausmustern"}
                        </Button>
                      </div>

                      {/*
                        Der Prompt der nächsten Fassung. Vorbelegt mit der
                        jüngsten — Weiterschreiben ist der Normalfall, bei null
                        anzufangen die Ausnahme.
                      */}
                      <div>
                        <Label htmlFor={`prompt-${rezept.id}`}>
                          Prompt der nächsten Fassung
                        </Label>
                        <Textarea
                          id={`prompt-${rezept.id}`}
                          className="mt-1.5 font-mono text-[12.5px]"
                          rows={10}
                          value={entwurf?.prompt ?? ""}
                          onChange={(ereignis) =>
                            setzeEntwurf((vorher) => ({
                              prompt: ereignis.target.value,
                              werkzeuge: vorher?.werkzeuge ?? [],
                              notiz: vorher?.notiz ?? "",
                            }))
                          }
                        />
                      </div>

                      <div>
                        <Mikrolabel grad={11}>
                          Erlaubte Lesewerkzeuge
                        </Mikrolabel>
                        {/*
                          Ehrlich benannt: Die Auswahl bindet keine Werkzeuge,
                          sie wird als Satz an den Prompt gehängt
                          („Erlaubte Lesewerkzeuge: …", durchsicht-service.ts).
                          Wer hier klickt, sagt dem Modell, was es nachschlagen
                          darf — er schaltet nichts frei.
                        */}
                        <p className="mt-1 max-w-[56ch] text-[12.5px] font-light leading-relaxed text-tinte3">
                          Wird dem Prompt als Satz angehängt. Das Modell
                          erfährt dadurch, was es nachschlagen darf.
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(werkzeuge.data ?? []).map((werkzeug) => {
                            const aktiv =
                              entwurf?.werkzeuge.includes(werkzeug.name) ??
                              false;
                            return (
                              <button
                                key={werkzeug.name}
                                type="button"
                                aria-pressed={aktiv}
                                title={werkzeug.beschreibung}
                                onClick={() =>
                                  setzeEntwurf((vorher) => {
                                    const bisher = vorher?.werkzeuge ?? [];
                                    return {
                                      prompt: vorher?.prompt ?? "",
                                      notiz: vorher?.notiz ?? "",
                                      werkzeuge: bisher.includes(werkzeug.name)
                                        ? bisher.filter(
                                            (n) => n !== werkzeug.name,
                                          )
                                        : [...bisher, werkzeug.name],
                                    };
                                  })
                                }
                                className={cx(
                                  "border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150 ease-aus",
                                  aktiv
                                    ? "border-akzent bg-papier text-tinte"
                                    : "border-linie2 text-tinte3 hover:border-linie hover:text-tinte2",
                                  focusRing,
                                )}
                              >
                                {werkzeug.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor={`notiz-${rezept.id}`}>
                          Warum diese Fassung
                        </Label>
                        <Input
                          id={`notiz-${rezept.id}`}
                          className="mt-1.5"
                          placeholder="Zum Beispiel: Prüft jetzt auch auf fehlende Tests."
                          value={entwurf?.notiz ?? ""}
                          onChange={(ereignis) =>
                            setzeEntwurf((vorher) => ({
                              prompt: vorher?.prompt ?? "",
                              werkzeuge: vorher?.werkzeuge ?? [],
                              notiz: ereignis.target.value,
                            }))
                          }
                        />
                      </div>

                      <Button
                        onClick={() => {
                          if (!entwurf?.prompt.trim()) return;
                          fassungAnlegen.mutate({
                            rezeptId: rezept.id,
                            prompt: entwurf.prompt,
                            werkzeuge: entwurf.werkzeuge,
                            ...(entwurf.notiz.trim()
                              ? { notiz: entwurf.notiz.trim() }
                              : {}),
                          });
                        }}
                        disabled={
                          fassungAnlegen.isPending ||
                          !entwurf?.prompt.trim() ||
                          /* Unverändert speichern legte eine Fassung ohne Aussage an. */
                          (entwurf.prompt === neueste?.prompt &&
                            entwurf.werkzeuge.join() ===
                              (neueste?.werkzeuge ?? []).join())
                        }
                        isLoading={fassungAnlegen.isPending}
                        loadingText="Speichere …"
                      >
                        Als Fassung {(neueste?.version ?? 0) + 1} speichern
                      </Button>

                      {/* Verlauf — lesbar, nicht änderbar. */}
                      {rezept.fassungen.length > 0 ? (
                        <div>
                          <Rubrik anzahl={`${rezept.fassungen.length}`}>
                            Bisherige Fassungen
                          </Rubrik>
                          <ul role="list" className="space-y-2.5">
                            {rezept.fassungen.map((fassung) => (
                              <li
                                key={fassung.id}
                                className="bg-wisch px-4 py-3"
                              >
                                <div className="flex flex-wrap items-baseline gap-x-3">
                                  <Mikrolabel grad={11} zahl betont>
                                    Fassung {fassung.version}
                                  </Mikrolabel>
                                  <Mikrolabel grad={11}>
                                    {zeitpunkt(fassung.angelegtAm)}
                                  </Mikrolabel>
                                  {fassung.notiz ? (
                                    <span className="text-[12.5px] text-tinte3">
                                      {fassung.notiz}
                                    </span>
                                  ) : null}
                                </div>
                                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[11.5px] leading-[1.6] text-tinte2">
                                  {fassung.prompt}
                                </pre>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-6 flex flex-wrap items-end gap-2.5">
          <div className="min-w-[260px] flex-1">
            <Label htmlFor="rezept-neu">Neues Rezept</Label>
            <Input
              id="rezept-neu"
              className="mt-1.5"
              placeholder="Name, zum Beispiel „Sicherheit“"
              value={neuerName}
              onChange={(ereignis) => setzeNeuerName(ereignis.target.value)}
            />
          </div>
          <Button
            onClick={() => anlegen.mutate(neuerName.trim())}
            disabled={anlegen.isPending || neuerName.trim().length === 0}
            isLoading={anlegen.isPending}
            loadingText="Lege an …"
          >
            Anlegen
          </Button>
          <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-xs text-tinte3">
            <input
              type="checkbox"
              className="size-3.5 accent-akzent"
              checked={mitArchiv}
              onChange={(ereignis) => setzeMitArchiv(ereignis.target.checked)}
            />
            Ausgemusterte zeigen
          </label>
        </div>
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
